import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";

import { useConsumerAuth } from "../context/ConsumerAuthContext";
import consumerApi from "../lib/consumerApi";

export default function PremiumPredictions({ competitionId }) {
  const auth = useConsumerAuth();
  if (!auth?.ready || !auth?.account?.subscription?.entitlement_active || !auth.account.subscription.capabilities?.predictions) return null;
  return <OwnedPrediction key={`${auth.account.user?.user_id}:${competitionId}`} competitionId={competitionId} />;
}

function OwnedPrediction({ competitionId }) {
  const [revision, setRevision] = useState(0);
  const [loaded, setLoaded] = useState(null);
  useEffect(() => {
    let active = true;
    consumerApi.get(`/premium/predictions/${encodeURIComponent(competitionId)}`).then(({ data }) => {
      if (active) setLoaded({ revision, data, error: null });
    }).catch(() => { if (active) setLoaded({ revision, data: null, error: "Prediction evidence could not be checked." }); });
    return () => { active = false; };
  }, [competitionId, revision]);
  const result = loaded?.revision === revision ? loaded : null;
  const data = result?.data;
  const estimate = data?.status === "available" ? data.estimate : null;
  const evaluation = estimate ? data.evaluation : null;
  return <section className="mt-8 border-y border-white/15 py-5 text-white" data-testid="premium-predictions">
    <h2 className="font-display text-2xl font-semibold">Predicted final odds</h2>
    {!result ? <p className="mt-3 text-sm" role="status">Checking prediction evidence...</p> : result.error ? <div className="mt-3 flex items-center gap-3">
      <p role="alert" className="text-sm">{result.error}</p>
      <button type="button" aria-label="Retry prediction" title="Retry prediction" className="min-h-10 min-w-10 rounded border border-white/20 p-2" onClick={() => setRevision(value => value + 1)}><RefreshCw size={18} /></button>
    </div> : <>
      {!estimate ? <p className="mt-3 text-sm" role="status">{data?.message || "No supported estimate is available."}</p> : <>
        <p className="mt-3 text-xl font-semibold">{estimate.odds_display} <span className="text-sm font-normal">estimated</span></p>
        <p className="mt-2 text-sm">Estimated final pool: {estimate.final_entries.toLocaleString("en-GB")} entries</p>
        <p className="mt-2 text-sm">Empirical range: {estimate.lower_entries.toLocaleString("en-GB")} to {estimate.upper_entries.toLocaleString("en-GB")} entries</p>
        <p className="mt-2 text-sm">Observed {new Date(estimate.observed_at).toLocaleString("en-GB")} / {estimate.observation_count} observations</p>
        {evaluation && <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
          <div><dt>Training / held-out competitions</dt><dd>{evaluation.training_competition_count} / {evaluation.heldout_competition_count}</dd></div>
          <div><dt>Held-out error (% of cap)</dt><dd>{(evaluation.mae_fraction_of_cap * 100).toFixed(1)}%</dd></div>
          <div><dt>No-more-sales / full-cap error</dt><dd>{(evaluation.no_more_sales_mae_fraction_of_cap * 100).toFixed(1)}% / {(evaluation.full_cap_mae_fraction_of_cap * 100).toFixed(1)}%</dd></div>
          <div><dt>Measured interval coverage</dt><dd>{(evaluation.heldout_interval_coverage * 100).toFixed(1)}%</dd></div>
        </dl>}
      </>}
      <p className="mt-4 text-xs leading-relaxed text-[var(--cc-text-muted)]">{data?.disclosure}</p>
    </>}
  </section>;
}
